import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { DollarSign, User, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

import API from '../api_config';

const formatCLP = (amount) => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0
  }).format(amount);
};

export default function AccountsReceivablePage() {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [openPayment, setOpenPayment] = useState(false);

  const fetchAccounts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/customer-accounts`);
      const filteredAccounts = response.data.filter(acc => acc.balance > 0);
      setAccounts(filteredAccounts);
    } catch (error) {
      toast.error('Error al cargar cuentas');
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!selectedAccount || !paymentAmount) return;

    try {
      await axios.post(`${API}/customer-accounts/${selectedAccount.customer_id}/payment`, {
        amount: Math.round(parseFloat(paymentAmount)),
        payment_date: new Date().toISOString(),
        payment_method: paymentMethod
      });
      toast.success('Pago registrado exitosamente');
      setOpenPayment(false);
      setPaymentAmount('');
      setPaymentMethod('efectivo');
      setSelectedAccount(null);
      fetchAccounts();
    } catch (error) {
      toast.error('Error al registrar pago');
    }
  };

  const openPaymentDialog = (account) => {
    setSelectedAccount(account);
    setPaymentAmount(account.balance.toString());
    setOpenPayment(true);
  };

  const totalReceivable = accounts.reduce((sum, acc) => sum + acc.balance, 0);

  return (
    <div className="space-y-6" data-testid="accounts-receivable-page">
      <div className="flex items-center space-x-3">
        <div className="p-3 bg-orange-600 rounded-2xl">
          <DollarSign className="h-8 w-8 text-white" />
        </div>
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Cuentas por Cobrar (Fiado)</h1>
          <p className="text-muted-foreground mt-1">
            Gestiona los pagos pendientes de clientes
          </p>
        </div>
      </div>

      {/* Summary Card */}
      <Card className="bg-gradient-to-br from-orange-50 to-yellow-50 dark:from-orange-950/20 dark:to-yellow-950/20 border-orange-200 dark:border-orange-800">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Total por Cobrar</p>
              <h2 className="text-4xl font-bold font-mono text-orange-600 dark:text-orange-400">
                {formatCLP(totalReceivable)}
              </h2>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground mb-1">Clientes con Deuda</p>
              <p className="text-3xl font-bold">{accounts.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Accounts List */}
      <div className="space-y-4">
        {accounts.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12 text-muted-foreground">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No hay cuentas por cobrar pendientes</p>
            </CardContent>
          </Card>
        ) : (
          accounts.map((account) => (
            <Card
              key={account.id}
              className="hover:shadow-lg transition-shadow"
              data-testid={`account-${account.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                        <User className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">
                          {account.customer?.name || 'Cliente desconocido'}
                        </h3>
                        {account.customer?.rut && (
                          <p className="text-sm text-muted-foreground font-mono">
                            RUT: {account.customer.rut}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Saldo Pendiente</p>
                        <p className="text-xl font-bold font-mono text-orange-600 dark:text-orange-400">
                          {formatCLP(account.balance)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Transacciones</p>
                        <p className="text-xl font-bold">{account.transactions?.length || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Último Pago</p>
                        <p className="text-sm font-medium">
                          {account.last_payment_date
                            ? new Date(account.last_payment_date).toLocaleDateString('es-CL')
                            : 'Sin pagos'}
                        </p>
                      </div>
                    </div>

                    {account.transactions && account.transactions.length > 0 && (
                      <div className="bg-muted/50 rounded-lg p-3">
                        <p className="text-xs font-semibold mb-2">Últimas Compras al Fiado:</p>
                        <div className="space-y-1">
                          {account.transactions.slice(-3).reverse().map((trans, idx) => (
                            <div key={idx} className="flex justify-between text-xs">
                              <span className="text-muted-foreground">
                                {new Date(trans.date).toLocaleDateString('es-CL')}
                              </span>
                              <span className="font-mono font-medium">
                                {formatCLP(trans.amount)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="ml-6">
                    <Button
                      onClick={() => openPaymentDialog(account)}
                      className="rounded-full shadow-lg"
                      data-testid={`pay-btn-${account.id}`}
                    >
                      <DollarSign className="h-4 w-4 mr-2" />
                      Registrar Pago
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Payment Dialog */}
      <Dialog open={openPayment} onOpenChange={setOpenPayment}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Pago</DialogTitle>
          </DialogHeader>
          {selectedAccount && (
            <form onSubmit={handlePayment} className="space-y-4">
              <div className="bg-muted p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Cliente</p>
                <p className="font-semibold text-lg">
                  {selectedAccount.customer?.name || 'Cliente desconocido'}
                </p>
                <p className="text-xs text-muted-foreground mt-2 mb-1">Saldo Actual</p>
                <p className="text-2xl font-bold font-mono text-orange-600 dark:text-orange-400">
                  {formatCLP(selectedAccount.balance)}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Método de Pago</Label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full h-10 rounded-lg border border-input bg-background px-3 py-2 text-sm"
                  data-testid="payment-method-select"
                >
                  <option value="efectivo">Efectivo</option>
                  <option value="tarjeta debito">Tarjeta de Débito</option>
                  <option value="tarjeta credito">Tarjeta de Crédito</option>
                  <option value="transferencia">Transferencia</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label>Monto a Pagar (CLP)</Label>
                <Input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  onBlur={(e) => setPaymentAmount(Math.round(parseFloat(e.target.value || 0)).toString())}
                  required
                  min="1"
                  max={selectedAccount.balance}
                  data-testid="payment-amount-input"
                />
                <p className="text-xs text-muted-foreground">
                  Saldo después del pago: {formatCLP(selectedAccount.balance - Math.round(parseFloat(paymentAmount || 0)))}
                </p>
              </div>

              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpenPayment(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" data-testid="confirm-payment-btn">
                  Confirmar Pago
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}